import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { pricing_plan_data, user_add_to_cart } from "../../api/Product";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function HostingPlans() {
  const [pricingList, setPricingList] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const navigate = useNavigate();

  const isCustomOrDedicated = (type) => {
    const lower = type?.toLowerCase();
    return lower?.includes("custom server") || lower?.includes("dedicated");
  };
  const isCustomServer = (type) => {
  return type?.toLowerCase().includes("custom server");
};

  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        const response = await pricing_plan_data();
        const plans = response?.data?.pricing_plans;
        if (Array.isArray(plans)) {
          setPricingList(plans);
        } else {
          toast.error("Invalid pricing data received.");
        }
      } catch (error) {
        console.error("Error fetching pricing plans:", error);
        toast.error("Failed to fetch pricing plans.");
      }
    };
    fetchPricingPlans();
  }, []);

  const toggleFeatures = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const handleAddToCart = useCallback(
    async (productId) => {
      try {
        const formData = new FormData();
        formData.append("product_id", productId);
        formData.append("quantity", "1");
        formData.append("order_type", "product");

        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const response = await user_add_to_cart(headers, formData);
        const cartId = response?.data?.data?.id;

        if (response?.data?.status == "200") {
          localStorage.setItem("add_cart_id", cartId);
          navigate("/cart");
        } else if (response?.response?.data?.status == "401") {
          toast.error("Please log in to access this page.");
          setTimeout(() => navigate("/login"), 2000);
        }
      } catch (error) {
        console.error("Add to cart failed:", error);
        toast.error("An error occurred while adding to cart.");
      }
    },
    [navigate]
  );

  // ✅ Split top and bottom plans
  const topPlans = pricingList
    .filter((item) => !isCustomOrDedicated(item?.type))
    .slice(0, 4);

  const bottomPlans = [
    ...pricingList.filter((item) => isCustomOrDedicated(item?.type)),
    ...pricingList.filter((item) => !isCustomOrDedicated(item?.type)).slice(4),
  ];

  // ✅ Render a plan card
  const PlanCard = ({ item, index }) => {
    const isExpanded = expandedIndex === index;
    const displayedFeatures = isExpanded
      ? item?.features
      : item?.features?.slice(0, 4);

    return (
      <div
        key={item.id || index}
        className="flex flex-col w-full sm:w-[260px] p-4 space-y-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
      >
        <div className="pb-6 space-y-2 border-b">
          <h2 className="text-2xl font-bold text-gray-800">{item?.type}</h2>
          <p className="text-sm text-gray-500 line-clamp-1">{item?.title}</p>
        </div>

        {/* Hide price for Custom/Dedicated Server */}
        {!isCustomOrDedicated(item?.type) && (
          <div>
            <span className="text-4xl font-medium tracking-tight text-blue-500">
              <span className="text-gray-400">$</span>
              {item?.price}
            </span>
            <span className="text-gray-400">/{item?.billing_cycle}</span>
          </div>
        )}

        <ul className="flex-1 space-y-2 p-0">
          {displayedFeatures?.map((feature, i) => (
            <li key={i} className="flex items-start">
              <svg
                className="w-6 h-6 text-blue-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="ml-3 text-sm text-gray-700">{feature?.feature}</span>
            </li>
          ))}
        </ul>

        {item?.features?.length > 4 && (
          <button
            onClick={() => toggleFeatures(index)}
            className="mt-4 text-blue-600 font-semibold hover:underline focus:outline-none"
          >
            {isExpanded ? "View Less ▲" : "View More ▼"}
          </button>
        )}

        {isCustomServer(item?.type) ? (
          <a
            href="/contact-us"
            className="w-full px-4 py-2 text-white bg-blue-500 rounded-full hover:bg-blue-600 focus:outline-none text-center transition-all duration-300"
          >
            Contact Us
          </a>
        ) : (
          <button
            onClick={() => handleAddToCart(item?.id)}
            className="w-full px-4 py-2 text-white bg-blue-500 rounded-full hover:bg-blue-600 focus:outline-none transition-all duration-300"
          >
            Get Started
          </button>
        )}
      </div>
    );
  };

  return (
    <div id="hosting-plans" className="container mx-auto antialiased text-gray-900 bg-white">
      <ToastContainer style={{ marginTop: "120px" }} />

      <div className="px-4 py-10 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-1 items-center">
          <div className="mx-auto max-w-2xl text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gray-900 border border-blue-800/30 mb-4">
              <span className="text-sm font-medium text-white">
                Perfect hosting plan for your next site
              </span>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Our Plans
            </h2>
          </div>

          {/* Top Row */}
          <div className="flex flex-wrap justify-center mt-10 gap-6">
            {topPlans.map((item, index) => (
              <PlanCard key={item.id || index} item={item} index={index} />
            ))}
          </div>

          {/* Bottom Row */}
          {bottomPlans.length > 0 && (
            <div className="flex flex-wrap justify-center mt-10 gap-6">
              {bottomPlans.map((item, index) => (
                <PlanCard key={item.id || index} item={item} index={4 + index} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
